import React, { useState } from "react";
import { useLanguage } from "../../App";
import { AVATAR_CHARACTERS, getCharactersByGender } from "../../config/characters";
import { Card } from "../ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../ui/carousel";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";

const CharacterSelector = ({ selectedCharacter, onSelect, size = "md", gender = null }) => {
  const { language } = useLanguage();
  const isEnglish = language === "en";

  // Filter karakter berdasarkan gender
  const characters = getCharactersByGender(gender);

  const theme = gender === "male"
    ? { gradient: "from-sky-500 to-indigo-600", soft: "from-sky-50 to-indigo-50", ring: "ring-sky-500", text: "text-sky-600" }
    : { gradient: "from-pink-500 to-rose-600", soft: "from-pink-50 to-rose-50", ring: "ring-pink-500", text: "text-pink-600" };

  const sizeClasses = {
    sm: {
      container: "w-16 h-16 sm:w-20 sm:h-20",
      check: "w-4 h-4",
      name: "text-[11px] sm:text-sm"
    },
    md: {
      container: "w-24 h-24 sm:w-28 sm:h-28",
      check: "w-5 h-5",
      name: "text-sm sm:text-base"
    },
    lg: {
      container: "w-32 h-32 sm:w-36 sm:h-36",
      check: "w-6 h-6",
      name: "text-sm sm:text-base"
    }
  };

  const currentSize = sizeClasses[size] || sizeClasses.md;

  // Tampilkan pesan jika gender belum dipilih
  if (!gender) {
    return (
      <div className="space-y-3 sm:space-y-4">
        <h3 className="text-sm sm:text-base font-semibold">
          {isEnglish ? "Pilih Karaktermu" : "Pilih Karaktermu"}
        </h3>
        <Card className="p-4 sm:p-6 text-center">
          <p className="text-sm text-muted-foreground">
            {isEnglish ? "Silakan pilih jenis kelamin terlebih dahulu" : "Silakan pilih jenis kelamin terlebih dahulu"}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="rounded-3xl bg-gradient-to-b from-blue-600 to-indigo-600 text-white px-4 py-5 sm:px-6 sm:py-6">
        <div className="flex items-center justify-between">
          <h2 className="text-base sm:text-lg font-bold">
            {isEnglish ? "Hand-pick Your Character" : "Pilih Karakter Anda"}
          </h2>
        </div>
        <div className="mt-4 rounded-3xl bg-background text-foreground px-4 py-3 sm:px-5 sm:py-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">{isEnglish ? "Explore" : "Jelajahi"}</span>
            <button className="text-xs sm:text-sm text-primary hover:underline">
              {isEnglish ? "View More" : "Lihat Semua"}
            </button>
          </div>
        </div>
      </div>

      {selectedCharacter && (
        <div className={`rounded-2xl p-3 sm:p-4 bg-gradient-to-br ${theme.soft} dark:from-background dark:to-background border`}>
          <div className={`relative w-full h-64 sm:h-80 md:h-96 rounded-2xl overflow-hidden bg-gradient-to-br ${theme.gradient} shadow-xl`}>
            <div className="absolute inset-0 opacity-20 mix-blend-soft-light" />
            <div className="p-4 sm:p-6 md:p-8 flex items-center justify-center h-full">
              <AnimatePresence mode="wait">
                <motion.img
                  key={selectedCharacter}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  src={(getCharactersByGender(gender).find(c => c.id === selectedCharacter))?.image}
                  alt="Selected Avatar"
                  className="w-full h-full object-contain drop-shadow-xl"
                />
              </AnimatePresence>
            </div>
          </div>
          <div className="mt-3 sm:mt-4 text-center">
            <span className={`text-sm sm:text-base font-semibold ${theme.text}`}>
              {isEnglish ? "Avatar Terpilih" : "Avatar Terpilih"}
            </span>
          </div>
        </div>
      )}

      <div className="relative">
        <Carousel className="w-full" opts={{ align: "center", containScroll: 'trimSnaps' }}>
          <CarouselContent className="py-3 px-3 rounded-3xl border bg-background">
            {characters.map((character) => {
              const isSelected = selectedCharacter === character.id;
              return (
                <CarouselItem key={character.id} className="basis-[220px] sm:basis-[260px] md:basis-[280px] pr-3 sm:pr-4">
                  <motion.div
                    className={`
                      cursor-pointer transition-all duration-300
                      ${isSelected
                        ? `ring-2 ${theme.ring} ring-offset-2 shadow-xl scale-105 bg-gradient-to-br from-yellow-300 via-amber-300 to-orange-300`
                        : "opacity-80 hover:opacity-100 hover:shadow-md"
                      }
                    `}
                    onClick={() => onSelect(character.id)}
                    layout
                    initial={false}
                    animate={isSelected ? { scale: 1.05 } : { scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 300, damping: 24 }}
                  >
                    <div className="p-3 sm:p-4">
                      <div className="relative">
                        <div
                          className={`
                            ${isSelected ? "w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56" : currentSize.container}
                            mx-auto rounded-2xl
                            ${isSelected ? "bg-transparent" : "bg-card"}
                            flex items-center justify-center overflow-hidden
                            ${isSelected ? "border-0" : "border-2 border-border"}
                          `}
                        >
                          <img
                            src={character.image}
                            alt={isEnglish ? character.name_en : character.name}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `
                                <span class="text-2xl sm:text-3xl">${gender === 'male' ? '👨' : '👩'}</span>
                              `;
                            }}
                          />
                        </div>
                        {isSelected && (
                          <div className={`
                            absolute -top-1 -right-1 rounded-full
                            bg-amber-500 text-white flex items-center justify-center
                            ${currentSize.check} shadow-md
                          `}>
                            <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                          </div>
                        )}
                      </div>
                      <p className={`
                        text-center ${currentSize.name} mt-1.5
                        font-semibold ${isSelected ? "text-amber-700" : "text-muted-foreground"} whitespace-nowrap overflow-hidden text-ellipsis
                      `}>
                        {isEnglish ? character.name_en : character.name}
                      </p>
                    </div>
                  </motion.div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <CarouselPrevious className="hidden sm:flex -left-10" />
          <CarouselNext className="hidden sm:flex -right-10" />
        </Carousel>
      </div>
    </div>
  );
};

export default CharacterSelector;
